// src/App.js
import React, {useEffect} from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { Toaster } from 'react-hot-toast';
import PrimeMarketsBanner from './components/TradeBanner'
// — Marketing site components/pages —
import Navbar from './components/Home/Navbar';
import FooterComponent from './components/Home/Footer';
import CookieConsent from './components/CookieConsent';
import usePageTitle from './hooks/usePageTitle';
import SEO from './components/SEO';
// Lazy load heavy components that aren't immediately needed
const Chatbot = React.lazy(() => import('./components/Home/Chatbot'));
const InvestmentBanner = React.lazy(() => import('./components/Home/InvestmentBanner'));
// Lazy load components for better performance
const Home = React.lazy(() => import('./pages/Home/Home'));
const TradingPlatform = React.lazy(() => import('./pages/Home/primemakets'));
const Forex = React.lazy(() => import('./pages/Home/Forex'));
const Stocks = React.lazy(() => import('./pages/Home/Stocks'));
const Indices = React.lazy(() => import('./pages/Home/Indices'));
const Commodities = React.lazy(() => import('./pages/Home/Commodities'));
const Primemarketx = React.lazy(() => import('./pages/Home/PrimemarketsX'));
const Accounts = React.lazy(() => import('./pages/Home/Accounts'));
const About = React.lazy(() => import('./pages/Home/AboutPrimeMakets'));
const Reviews = React.lazy(() => import('./pages/Home/Reviews'));
const News = React.lazy(() => import('./pages/Home/News'));
const FundsAndWithdrawals = React.lazy(() => import('./pages/Home/FundsAndWithdrawals'));
const PersonalSupportManagers = React.lazy(() => import('./pages/Home/PersonalSupportManagers'));
const FAQs = React.lazy(() => import('./pages/Home/FAQs'));
const EconomicCalendar = React.lazy(() => import('./pages/Home/EconomicCalendar'));
const Verification = React.lazy(() => import('./pages/Home/Verification'));
const AccountTypes = React.lazy(() => import('./pages/Home/AccountTypes'));
const TradeInside = React.lazy(() => import('./pages/Home/TradeInside'));
const TradingChargesPage = React.lazy(() => import('./pages/Home/OurChanges'));
const CFDTradingPage = React.lazy(() => import('./pages/Home/CfdTrading'));
const Crypto = React.lazy(() => import('./pages/Home/Crypto'));
const MobileApp = React.lazy(() => import('./pages/Home/MobileApp'));

const pageVariants = {
  initial: {
    opacity: 0,
    x: -50,
    scale: 0.95
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    x: 50,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

const slideVariants = {
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

const fadeVariants = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: "easeInOut"
    }
  }
};

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );
}

function AnimatedPage({ children, variant = "default" }) {
  const variants = {
    default: pageVariants,
    slide: slideVariants,
    fade: fadeVariants
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants[variant]}
      className="animated-page"
      style={{ width: '100%', minHeight: '100vh' }}
    >
      <React.Suspense fallback={<LoadingFallback />}>
        {children}
      </React.Suspense>
    </motion.div>
  );
}
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, [pathname]);
  return null;
}

function ExternalRedirect({ url }) {
  useEffect(() => {
    window.location.href = url;
  }, [url]);
  return <div>Redirecting...</div>;
}
function LayoutWithNavFooter() {
  const location = useLocation();
  const seoData = usePageTitle();
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={location.pathname}
        structuredData={seoData.structuredData}
      />
      <Navbar />
      <main>
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname}>
            <Outlet />
          </motion.div>
        </AnimatePresence>
        <React.Suspense fallback={null}>
          <Chatbot />
        </React.Suspense>
        <React.Suspense fallback={null}>
          <InvestmentBanner />
          <PrimeMarketsBanner/>
        </React.Suspense>
      </main>
      <FooterComponent />
    </>
  );
}

// function AdminLayout() {
//   return (
//     <div className="min-h-screen bg-gray-100">
//       <Outlet />
//     </div>
//   );
// }

function AppRoutes() {
  // const token = localStorage.getItem('token');
  // const navigate = useNavigate();
  // const user = token ? decodeToken(token) : null;

  // const requireAuth = (children) =>
  //   token ? children : <Navigate to="/admin/login" replace />;

  // const goToAdmin = (section) => {
  //   navigate(`/admin/${section}`, { replace: true });
  // };

  // const logout = () => {
  //   localStorage.removeItem('token');
  //   navigate('/admin/login', { replace: true });
  // };

  return (
    <Routes>
      <Route element={<LayoutWithNavFooter />}>
        <Route path="/" element={<AnimatedPage variant="fade"><Home /></AnimatedPage>} />
        <Route path="/forex" element={<AnimatedPage variant="fade"><Forex /></AnimatedPage>} />
        <Route path="/stocks" element={<AnimatedPage variant="fade"><Stocks /></AnimatedPage>} />
        <Route path="/indices" element={<AnimatedPage><Indices /></AnimatedPage>} />
        <Route path="/commodities" element={<AnimatedPage variant="fade"><Commodities /></AnimatedPage>} />
        <Route path="/primemarketx" element={<AnimatedPage><Primemarketx /></AnimatedPage>} />
        <Route path="/accounts" element={<AnimatedPage variant="fade"><Accounts /></AnimatedPage>} />
        <Route path="/about-prime-markets" element={<AnimatedPage><About /></AnimatedPage>} />
        <Route path="/reviews" element={<AnimatedPage variant="fade"><Reviews /></AnimatedPage>} />
        <Route path="/news" element={<AnimatedPage><News /></AnimatedPage>} />
        <Route path="/funding-and-withdrawals" element={<AnimatedPage variant="fade"><FundsAndWithdrawals /></AnimatedPage>} />
        <Route path="/personal-support-managers" element={<AnimatedPage><PersonalSupportManagers /></AnimatedPage>} />
        <Route path="/faqs" element={<AnimatedPage variant="fade"><FAQs /></AnimatedPage>} />
        <Route path="/economic-calendar" element={<AnimatedPage><EconomicCalendar /></AnimatedPage>} />
        <Route path="/verification" element={<AnimatedPage variant="fade"><Verification /></AnimatedPage>} />
        <Route path="/account-types" element={<AnimatedPage><AccountTypes /></AnimatedPage>} />  
        <Route path="/cfd-trading" element={<AnimatedPage variant="fade"><CFDTradingPage /></AnimatedPage>} />
        <Route path="/cfd-trading/our-changes" element={<AnimatedPage><TradingChargesPage /></AnimatedPage>} />
        <Route path="/trade-insider" element={<AnimatedPage variant="fade"><TradeInside /></AnimatedPage>} />
        <Route path='/primemarket' element={<AnimatedPage><TradingPlatform /></AnimatedPage>} />
        <Route path='/crypto' element={<AnimatedPage><Crypto /></AnimatedPage>} />
        <Route path='/mobile-app' element={<AnimatedPage><MobileApp /></AnimatedPage>} />
        <Route path="/get-started" element={<Navigate to="/accounts" replace />} />
        <Route path="/myprimemarket" element={<ExternalRedirect url="https://prime.primemarkets.co.za/primemarket" />} />
      </Route>

      {/* <Route path="/admin/login" element={<Login />} />

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />

        <Route
          path="dashboard"
          element={requireAuth(
            <Dashboard goTo={goToAdmin} logout={logout} />
          )}
        />

        <Route
          path="profile"
          element={requireAuth(
            <Profile user={user} goTo={goToAdmin} logout={logout} />
          )}
        />

        <Route
          path="cms"
          element={requireAuth(
            <CMS goTo={goToAdmin} logout={logout} />
          )}
        />

        <Route
          path="ai"
          element={requireAuth(
            <AIContent goTo={goToAdmin} logout={logout} />
          )}
        />

      </Route> */}
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Toaster position="bottom-center" toastOptions={{ duration: 3000 }} />
        <AppRoutes />
        <CookieConsent />
      </Router>
    </HelmetProvider>
  );
}